/**
 * Общий словарь фронта: перечисления, расшифровки кодов и те немногие формы
 * ответов, которые нужны сразу нескольким экранам.
 *
 * Имена полей повторяют apps/api/src/db/schema/ буква в букву: Drizzle
 * отдаёт camelCase, менять его по дороге нельзя — иначе фронт и бэк начнут
 * расходиться молча.
 *
 * Форму ответа конкретного экрана держит сам экран, рядом с запросом: реестр
 * — в features/registry/, кабинет — в
 * api/cabinet.ts, очереди — в features/review/TaskQueuePanel.tsx, внешки — в
 * features/faceclaims/FaceClaimEdit.tsx. Здесь такие типы не дублируются: две копии одного
 * контракта расходятся на первой же правке бэкенда, и молча.
 */

import { t } from '../i18n';

/* ── Перечисления (зеркало pgEnum) ───────────────────────────────────────── */

export type UserRole = 'player' | 'form_moderator' | 'quest_master' | 'superadmin';

/** Кто видит вкладку «Задачи». Зеркало STAFF из apps/api/src/common/utils/roles.ts. */
export const STAFF_ROLES: readonly UserRole[] = ['form_moderator', 'quest_master', 'superadmin'];

/** Кто разбирает очередь анкет. */
export const FORM_MODERATOR_ROLES: readonly UserRole[] = ['form_moderator', 'superadmin'];

/** Кто подтверждает предметы, способности и дебафы. */
export const CONTENT_MODERATOR_ROLES: readonly UserRole[] = ['quest_master', 'superadmin'];

export const ADMIN_ROLES: readonly UserRole[] = ['superadmin'];

/** Зеркало `CHARACTER_KINDS`. Не путать с `UserRole` — та про права в приложении. */
export type CharacterKind = 'personnel' | 'resident' | 'professor';

export type Dorm = 'o1' | 'o2' | 'o3';

export type StudyClass = 'e1' | 'e2' | 'd1' | 'd2' | 'c' | 'b' | 'a';

/**
 * Общий жизненный цикл версии анкеты и версии имущества. У имущества draft не
 * встречается: заявка уходит на проверку сразу, черновиков там нет.
 */
export type ReviewStatus = 'draft' | 'pending' | 'approved' | 'rejected';

/**
 * Вид записи в таблице abilities. Зеркало ABILITY_KINDS.
 *
 * Это про то, ЧТО получил персонаж: умение или наложенное ограничение.
 * Откуда оно взялось — вопрос отдельный, и отвечает на него ссылка на
 * источник: дебаф можно схлопотать и вне квеста, а способность — заработать
 * в отыгрыше.
 *
 * Стартовой способности вид не назначается: она лежит девятым пунктом анкеты,
 * отдельной строки в таблице у неё не заводится вовсе.
 */
export type AbilityKind = 'ability' | 'debuff';

/**
 * Происхождение способности в общем списке — пометка для читателя, а не
 * колонка в базе.
 *
 * Хранение остаётся раздельным: стартовая способность — часть версионируемой
 * анкеты, выданная — самостоятельная запись со своим подтверждением, сроком
 * и картинкой. Единым список становится только на экране, и вот там у каждой
 * строки нужна метка, откуда она взялась. Значений ровно два: третьего
 * хранилища у способностей нет.
 */
export type AbilityOrigin = 'form' | 'granted';

export type ModerationTarget = 'form' | 'item' | 'pet' | 'ability';

export type ModerationVerdict = 'approve' | 'reject';

/* ── Расшифровки ─────────────────────────────────────────────────────────── */

export const ROLE_LABELS: Record<UserRole, string> = {
  player: t('api.role.player'),
  form_moderator: t('api.role.form_moderator'),
  quest_master: t('api.role.quest_master'),
  superadmin: t('api.role.superadmin'),
};

export const CHARACTER_KIND_LABELS: Record<CharacterKind, string> = {
  personnel: t('api.characterKind.personnel'),
  resident: t('api.characterKind.resident'),
  professor: t('api.characterKind.professor'),
};

/** Названия общежитий и классов держим здесь: в базе лежат только коды. */
/**
 * Общежитие обозначают буквой с номером — «О-1», как класс обозначают «D-1».
 * Полного названия («Первое общежитие») в интерфейсе нет нигде: оно не влезало
 * в кнопку фильтра, и рядом с ним завелась вторая, короткая запись — два имени
 * на одну вещь.
 */
export const DORM_LABELS: Record<Dorm, string> = {
  o1: t('api.dorm.o1'),
  o2: t('api.dorm.o2'),
  o3: t('api.dorm.o3'),
};

export const STUDY_CLASS_LABELS: Record<StudyClass, string> = {
  e1: t('api.studyClass.e1'),
  e2: t('api.studyClass.e2'),
  d1: t('api.studyClass.d1'),
  d2: t('api.studyClass.d2'),
  c: t('api.studyClass.c'),
  b: t('api.studyClass.b'),
  a: t('api.studyClass.a'),
};

/** Чего касается решение модерации. Нужен и журналу персонала, и ленте игрока. */
export const MODERATION_TARGET_LABELS: Record<ModerationTarget, string> = {
  form: t('api.moderationTarget.form'),
  item: t('api.moderationTarget.item'),
  pet: t('api.moderationTarget.pet'),
  ability: t('api.moderationTarget.ability'),
};

export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  draft: t('api.reviewStatus.draft'),
  pending: t('api.reviewStatus.pending'),
  approved: t('api.reviewStatus.approved'),
  rejected: t('api.reviewStatus.rejected'),
};

/**
 * Подписи происхождения: откуда запись взялась. Нужны там, где список общий —
 * в карточке персонажа и в кабинете игрока: без пометки стартовая способность
 * из анкеты и выданная квест-мастером сливаются в одну кучу.
 */
export const ABILITY_ORIGIN_LABELS: Record<AbilityOrigin, string> = {
  form: t('api.abilityOrigin.form'),
  granted: t('api.abilityOrigin.granted'),
};

/**
 * Подписи вида: что именно получил персонаж. Словарь отдельный от
 * происхождения, потому что и плоскости разные: вид есть только у выданных
 * записей, а у стартовой из анкеты его нет вовсе — спрашивать не у чего.
 *
 * Очередь квест-мастера показывает именно вид: происхождение там у всех строк
 * одинаковое, различает их только ссылка на источник.
 */
export const ABILITY_KIND_LABELS: Record<AbilityKind, string> = {
  ability: t('api.abilityKind.ability'),
  debuff: t('api.abilityKind.debuff'),
};

/* ── Анкета ──────────────────────────────────────────────────────────────── */

/**
 * Поля анкеты в порядке анкеты. Зеркало FORM_FIELDS из схемы: тот же список
 * приходит в diffFieldCodes, по нему рисуется «что изменилось».
 * Одиннадцатое поле — общежитие — живёт на персонаже: его ставит модератор.
 */
export const FORM_FIELDS = [
  'nameRu',
  'nameEn',
  'nicknames',
  'gender',
  'age',
  'familyText',
  'race',
  'appearanceText',
  'appearanceImageId',
  'personalityText',
  'biographyText',
  'abilities',
  'molesCount',
] as const;

export type FormField = (typeof FORM_FIELDS)[number];

/**
 * Стартовых способностей в анкете не больше двух — зеркало FORM_ABILITIES_MAX
 * в apps/api/src/modules/characters/constants.ts.
 *
 * Держит предел СЕРВЕР (@ArrayMaxSize в SaveFormDto): форма только не даёт в
 * него упереться. Разойдутся — игрок получит 400 там, где кнопка обещала, что
 * всё в порядке.
 */
export const FORM_ABILITIES_MAX = 2;

/**
 * Подписи полей анкеты. Единственный словарь на весь фронт: редактор игрока,
 * карточка в реестре и экран проверки обязаны называть поле одинаково —
 * иначе модератор пишет «поправьте семью», а игрок такого поля у себя не
 * видит.
 *
 * Текст скопирован буква в букву из FORM_FIELD_LABELS
 * в apps/api/src/modules/characters/constants.ts: те же подписи сервер кладёт в
 * diff, и разъезжаться им нельзя.
 */
export const FORM_FIELD_LABELS: Record<FormField, string> = {
  nameRu: t('api.formField.nameRu'),
  nameEn: t('api.formField.nameEn'),
  nicknames: t('api.formField.nicknames'),
  gender: t('api.formField.gender'),
  age: t('api.formField.age'),
  familyText: t('api.formField.familyText'),
  race: t('api.formField.race'),
  appearanceText: t('api.formField.appearanceText'),
  appearanceImageId: t('api.formField.appearanceImageId'),
  personalityText: t('api.formField.personalityText'),
  biographyText: t('api.formField.biographyText'),
  abilities: t('api.formField.abilities'),
  molesCount: t('api.formField.molesCount'),
};

/** Одна строка блока способностей внутри анкеты (jsonb-поле abilities). */
export interface FormAbilityEntry {
  title: string;
  description: string | null;
}

/**
 * Поля самой анкеты — то, что заполняет игрок. Список один на все места, где
 * анкета появляется: своя версия, версия в разборе, тело сохранения. Имена и
 * порядок совпадают с FORM_FIELDS выше и с FormContentView на сервере.
 *
 * Карточка реестра от него производная — там нет appearanceImageId, вместо
 * него приходит готовый адрес картинки.
 */
export interface FormFields {
  nameRu: string | null;
  nameEn: string | null;
  nicknames: string | null;
  gender: string | null;
  age: string | null;
  familyText: string | null;
  race: string | null;
  appearanceText: string | null;
  appearanceImageId: string | null;
  personalityText: string | null;
  biographyText: string | null;
  abilities: FormAbilityEntry[];
  molesCount: string | null;
}

/** Автор заявки, модератор решения, актор в журнале. Зеркало AuthorView. */
export interface Author {
  vkUserId: number;
  displayName: string | null;
}

/**
 * Стартовая способность в общем списке способностей — первый член
 * размеченного объединения по origin.
 *
 * Ни id строки, ни вида, ни ссылки на источник, ни срока действия, ни картинки
 * у неё нет: она живёт внутри версии анкеты и действует ровно пока эта версия
 * опубликована. Второй член объединения — выданная, origin: 'granted' — у
 * каждого экрана свой: в карточке реестра это подтверждённая способность, в
 * кабинете — строка заявки со статусом. Общего типа с необязательными полями
 * здесь нет намеренно: с ними компилятор перестал бы ловить обращение к сроку
 * действия у стартовой.
 */
export interface StartingAbility extends FormAbilityEntry {
  origin: 'form';
}

/* ── Общие кусочки ───────────────────────────────────────────────────────── */

/** Даты приходят строками ISO 8601: JSON других не знает. */
export type IsoDate = string;

/* ── Публичные настройки ─────────────────────────────────────────────────── */

/**
 * Тело POST /api/users/register. Номера страницы тут нет намеренно: сервер
 * берёт его из подписи параметров запуска, а не из того, что прислал экран.
 * Роли нет тем более — её ставит сервер, и лишнее поле отобьёт ValidationPipe.
 */
export interface RegisterBody {
  launchParams: string;
  displayName?: string | null;
}

/** Ответ той же ручки — зеркало UserRegisteredView. */
export interface RegisteredResponse {
  vkUserId: number;
  role: UserRole;
  displayName: string | null;
}

/**
 * Тело POST /api/users/me/delete-data. Номер страницы сервер, как и у записи,
 * берёт из подписи: у отозванного сессия есть, а аккаунта в ней нет.
 */
export interface DataDeletionBody {
  launchParams: string;
}

/* ── Сессия ──────────────────────────────────────────────────────────────── */

/** Зеркало SessionUser из apps/api/src/common/decorators.ts. */
export interface SessionUser {
  id: number;
  vkUserId: number;
  role: UserRole;
  /** false = исключён из вайтлиста: остаётся только чтение. */
  canWrite: boolean;
  /** Владелец удалил свои данные. Бан сюда не попадает: отозванного сессия не узнаёт вовсе. */
  deletedAt: IsoDate | null;
  characterId: number | null;
}

export interface SessionResponse {
  token: string;
  user: SessionUser | null;
  /** true = аккаунта нет в вайтлисте. Читать можно, писать нельзя. */
  guest: boolean;
  vkUserId: number;
}

export interface MeResponse {
  user: SessionUser | null;
  /**
   * Открыта ли сейчас самостоятельная запись. Едет в ответе «кто я», потому
   * что гостю больше ничего и не отвечают: в вайтлисте его нет, набор
   * настроек закрыт ролью, а решать, вступать или писать администрации, ему.
   */
  selfRegistration: boolean;
}

/* ── Свой аккаунт ────────────────────────────────────────────────────────── */

/**
 * Ответ GET /api/users/me — и PATCH туда же: настройки аккаунта отдаются одной
 * формой и на чтение, и на запись.
 *
 * Счётчик непрочитанных решений лежит здесь, а не в ответе кабинета персонажа:
 * отметка о прочтении хранится в read_marker, а та ключуется по аккаунту, и
 * персонажа в ней нет вовсе.
 *
 * Тип общий, а не по копии на экран: его читают профиль, хаб персонажа и
 * таббар — три копии одного контракта разъехались бы на первой правке бэкенда,
 * и молча.
 */
export interface MySettingsResponse {
  /** Скрыта ли ссылка на профиль ВК в публичном реестре. */
  hideVkProfile: boolean;
}

/**
 * Ответ GET /api/users/me: настройки плюс счётчик.
 *
 * Наследование, а не необязательное поле, и не один тип на оба направления:
 * PATCH возвращает ТОЛЬКО настройки, и если объявить unreadDecisions общим,
 * компилятор пропустит обращение к нему на ответе PATCH и вернёт undefined.
 * Прилетело бы это ровно в бейдж — единственное значение, которое читают
 * сразу три экрана.
 */
export interface MyAccountResponse extends MySettingsResponse {
  /** Решения по моим заявкам, которые я ещё не открывал. */
  unreadDecisions: number;
}

/* ── Модерация ───────────────────────────────────────────────────────────── */

/**
 * Ответ GET /api/review/queues: сколько заявок ждёт разбора.
 *
 * null у очереди — НЕ ноль: так сервер говорит «эта очередь вашей роли не
 * видна вовсе» (см. getQueueSizes в review.service.ts). Ноль показываем
 * цифрой, null не показываем совсем.
 */
export interface QueueCounters {
  forms: number | null;
  items: number | null;
  pets: number | null;
  abilities: number | null;
  /** Сумма видимых очередей: невидимые считаются нулём. */
  total: number;
}

/**
 * Цифры на вкладках таббара. Отдельного маршрута под них нет: App.tsx
 * собирает их из GET /api/review/queues и GET /api/users/me.
 */
export interface BadgeCounters {
  /** Решения по моим заявкам, которые я ещё не открывал (read_marker). */
  unreadDecisions: number;
  /** null — очереди не спрашивали: роль не та или сервер не ответил. */
  queue: QueueCounters | null;
}

/* ── Ошибки ──────────────────────────────────────────────────────────────── */

/**
 * Тело ошибки собирает common/filters/error-code.filter.ts на сервере.
 * Родного нестового error здесь нет: он пересказывал словами то же, что
 * говорят status_code и error_code. message бывает и строкой, и списком
 * от ValidationPipe.
 */
export interface ApiErrorBody {
  status_code?: number;
  message?: string | string[];
  /**
   * Машинное имя ошибки — снейк-кейс от имени класса исключения на сервере
   * (`PetNotFoundException` → `pet_not_found`). По нему ветвятся, а не по
   * тексту: текст английский и может смениться, код — нет.
   * Приходит у всего, что сервер бросает намеренно; у случайной пятисотки нет.
   */
  error_code?: string;
}

/** Конверт любого списка: сервер отдаёт списки только так, отдельных обёрток нет. */
export interface Page<T> {
  total: number;
  items: T[];
}
